import { useTranslate } from "@opensea/next-translate"
import { PhoneInput } from "@opensea/ui-kit/client"
import { useMfaEnrollment } from "@privy-io/react-auth"
import React, { useState } from "react"
import { Button } from "@/design-system/Button"
import { Form } from "@/design-system/Form"
import { FormControl } from "@/design-system/FormControl"
import { useForm } from "@/hooks/useForm"

type Props = {
  onPhoneNumber: (phoneNumber: string) => void
}

type FormData = {
  phoneNumber: string
}

const SUPPORTED_COUNTRIES = ["us", "ca"]

const US_CANADA_PHONE_NUMBER_VALIDATION =
  /^(\+1\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/

export function ManageSmsPhoneStep({ onPhoneNumber }: Props) {
  const t = useTranslate("mfa")
  const { initEnrollmentWithSms } = useMfaEnrollment()
  const [submitError, setSubmitError] = useState(false)

  const {
    formState: { errors, isSubmitting, isValid },
    register,
    handleSubmit,
  } = useForm<FormData>({
    mode: "onSubmit",
  })

  const onSubmit = handleSubmit(async ({ phoneNumber }) => {
    setSubmitError(false)
    try {
      await initEnrollmentWithSms({ phoneNumber })
      onPhoneNumber(phoneNumber)
    } catch {
      setSubmitError(true)
    }
  })

  return (
    <Form
      className="flex w-full grow flex-col justify-between"
      onSubmit={onSubmit}
    >
      <FormControl
        error={
          submitError
            ? t(
                "enrollment.sms.error",
                "Unable to send code to this phone number",
              )
            : errors.phoneNumber?.message
        }
        hideLabel
        label={t("enrollment.sms.label", "Phone number")}
      >
        <PhoneInput
          className="flex-1 bg-white p-0 dark:bg-component-gray-1"
          overrides={{
            Select: {
              className: "ml-[1px]",
            },
          }}
          supportedCountries={SUPPORTED_COUNTRIES}
          {...register("phoneNumber", {
            required: t("enrollment.sms.required", "Phone number is required."),
            pattern: {
              value: US_CANADA_PHONE_NUMBER_VALIDATION,
              message: t("enrollment.sms.invalid", "Invalid phone number"),
            },
          })}
        />
      </FormControl>
      <Button
        disabled={!isValid || isSubmitting}
        type="submit"
        variant="primary"
      >
        {t("enrollment.step.next", "Next")}
      </Button>
    </Form>
  )
}
